<template>
  <div class="card-issue-config">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>门店管理</el-breadcrumb-item>
      <el-breadcrumb-item>店主发卡配置</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 卡片区域 -->
    <el-card class="box-card">
      <!-- 操作区域 -->
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-plus" @click="showAddDialog" v-permission="'cardIssue:config:add'">添加店铺</el-button>
        <el-button type="success" icon="el-icon-setting" @click="goToCardTypeManage">卡片类型管理</el-button>
        <el-button icon="el-icon-refresh" @click="loadConfigList">刷新</el-button>
      </div>

      <!-- 配置列表 -->
      <el-table :data="configList" border stripe v-loading="loading" style="width: 100%">
        <el-table-column prop="storeId" label="ID" align="center"></el-table-column>
        <el-table-column prop="storeName" label="店铺名称" show-overflow-tooltip></el-table-column>
        <el-table-column label="状态" align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.isEnabled"
              :active-value="1"
              :inactive-value="0"
              @change="handleStatusChange(scope.row)"
            ></el-switch>
          </template>
        </el-table-column>
        <!-- 动态显示所有卡片类型的额度 -->
        <el-table-column
          v-for="cardType in enabledCardTypes"
          :key="cardType.cardType"
          :label="cardType.cardName"
          align="center"
          width="100"
        >
          <template slot-scope="scope">
            <span>{{ getCardQuota(scope.row, cardType.cardType) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ formatTime(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click="showEditDialog(scope.row)" v-permission="'cardIssue:config:edit'">编辑</el-button>
            <el-button type="danger" size="mini" @click="handleDelete(scope.row)" v-permission="'cardIssue:config:delete'">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 添加店铺对话框 -->
    <el-dialog title="添加店铺发卡配置" :visible.sync="addDialogVisible" width="500px" append-to-body>
      <el-form :model="addForm" label-width="100px">
        <el-form-item label="选择店铺">
          <el-select v-model="addForm.storeId" placeholder="请选择店铺" filterable style="width: 100%">
            <el-option
              v-for="store in availableStores"
              :key="store.id"
              :label="store.storename"
              :value="store.id"
              :disabled="store.configured"
            >
              <span>{{ store.storename }}</span>
              <span v-if="store.configured" style="color: #999; margin-left: 10px;">(已配置)</span>
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAdd" :loading="submitting">确定添加</el-button>
      </div>
    </el-dialog>

    <!-- 编辑配置对话框 -->
    <el-dialog title="编辑发卡配置" :visible.sync="editDialogVisible" width="700px" append-to-body>
      <el-form :model="editForm" label-width="100px">
        <el-form-item label="店铺名称">
          <el-input v-model="editForm.storeName" disabled></el-input>
        </el-form-item>
        <el-form-item label="发卡状态">
          <el-switch v-model="editForm.isEnabled" :active-value="1" :inactive-value="0"></el-switch>
          <span style="margin-left: 10px; color: #999;">{{ editForm.isEnabled === 1 ? '已开启' : '已关闭' }}</span>
        </el-form-item>
        <el-divider content-position="left">月度发卡额度配置</el-divider>
        <!-- 动态显示所有启用的卡片类型 -->
        <el-row :gutter="20">
          <el-col :span="12" v-for="cardType in enabledCardTypes" :key="cardType.cardType">
            <el-form-item :label="cardType.cardName">
              <el-input-number 
                v-model="editForm.cardQuotas[cardType.cardType]" 
                :min="0" 
                :max="500" 
                style="width: 120px;"
              ></el-input-number>
              <span style="margin-left: 8px; color: #666;">张/月</span>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleUpdate" :loading="submitting">保存修改</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CardIssueConfig',
  data() {
    return {
      loading: false,
      submitting: false,
      configList: [],
      allStores: [],
      enabledCardTypes: [], // 启用的卡片类型列表
      addDialogVisible: false,
      editDialogVisible: false,
      addForm: {
        storeId: null
      },
      editForm: {
        storeId: null,
        storeName: '',
        isEnabled: 1,
        cardQuotas: {} // 动态卡片额度配置
      }
    }
  },
  computed: {
    availableStores() {
      return this.allStores
    }
  },
  created() {
    this.loadCardTypes()
    this.loadConfigList()
    this.loadAllStores()
  },
  methods: {
    // 加载启用的卡片类型
    async loadCardTypes() {
      try {
        const res = await axios.get('/cardType/enabled')
        if (res.data.status === 0) {
          // 转换字段名：下划线转驼峰
          this.enabledCardTypes = (res.data.data || []).map(item => ({
            id: item.id,
            cardType: item.card_type,
            cardName: item.card_name,
            cardDays: item.card_days,
            sortOrder: item.sort_order,
            isEnabled: item.is_enabled ? 1 : 0
          }))
        }
      } catch (e) {
        console.error('加载卡片类型失败', e)
      }
    },

    // 跳转到卡片类型管理页面
    goToCardTypeManage() {
      this.$router.push('/card-type-manage')
    },

    // 从配置中获取指定卡片类型的额度
    getCardQuota(config, cardType) {
      if (config.cardQuotas) {
        try {
          const quotas = typeof config.cardQuotas === 'string' 
            ? JSON.parse(config.cardQuotas) 
            : config.cardQuotas
          return quotas[cardType] || 0
        } catch (e) {
          console.error('解析cardQuotas失败', e)
        }
      }
      // 兼容旧字段
      const oldFieldMap = {
        'year_card': config.yearCardLimit,
        'quarter_card': config.quarterCardLimit,
        'month_card': config.monthCardLimit,
        'three_day_card': config.threeDayCardLimit,
        'day_card': config.dayCardLimit
      }
      return oldFieldMap[cardType] || 0
    },

    async loadConfigList() {
      this.loading = true
      try {
        const res = await axios.get('/cardIssue/config/list')
        if (res.data.status === 0) {
          this.configList = res.data.data || []
        } else {
          this.$message.error(res.data.message || '加载失败')
        }
      } catch (e) {
        console.error('加载配置列表失败', e)
        this.$message.error('加载配置列表失败')
      } finally {
        this.loading = false
      }
    },

    async loadAllStores() {
      try {
        const res = await axios.get('/cardIssue/stores')
        if (res.data.status === 0) {
          this.allStores = res.data.data || []
        }
      } catch (e) {
        console.error('加载店铺列表失败', e)
      }
    },

    showAddDialog() {
      this.addForm.storeId = null
      this.loadAllStores()
      this.addDialogVisible = true
    },

    showEditDialog(row) {
      // 解析cardQuotas JSON
      let cardQuotas = {}
      if (row.cardQuotas) {
        try {
          cardQuotas = typeof row.cardQuotas === 'string' 
            ? JSON.parse(row.cardQuotas) 
            : row.cardQuotas
        } catch (e) {
          console.error('解析cardQuotas失败', e)
          // 使用旧字段作为兼容
          cardQuotas = {
            'year_card': row.yearCardLimit || 0,
            'quarter_card': row.quarterCardLimit || 0,
            'month_card': row.monthCardLimit || 0,
            'three_day_card': row.threeDayCardLimit || 0,
            'day_card': row.dayCardLimit || 0
          }
        }
      } else {
        // 使用旧字段
        cardQuotas = {
          'year_card': row.yearCardLimit || 0,
          'quarter_card': row.quarterCardLimit || 0,
          'month_card': row.monthCardLimit || 0,
          'three_day_card': row.threeDayCardLimit || 0,
          'day_card': row.dayCardLimit || 0
        }
      }
      
      this.editForm = {
        storeId: row.storeId,
        storeName: row.storeName,
        isEnabled: row.isEnabled,
        cardQuotas: cardQuotas
      }
      this.editDialogVisible = true
    },

    async handleAdd() {
      if (!this.addForm.storeId) {
        this.$message.warning('请选择店铺')
        return
      }

      this.submitting = true
      try {
        const res = await axios.post('/cardIssue/config/add', {
          storeId: this.addForm.storeId,
          createBy: window.sessionStorage.getItem('admin_token') || 'admin'
        })
        if (res.data.status === 0) {
          this.$message.success('添加成功')
          this.addDialogVisible = false
          this.loadConfigList()
          this.loadAllStores()
        } else {
          this.$message.error(res.data.message || '添加失败')
        }
      } catch (e) {
        console.error('添加失败', e)
        this.$message.error('添加失败')
      } finally {
        this.submitting = false
      }
    },

    async handleUpdate() {
      this.submitting = true
      try {
        const res = await axios.post('/cardIssue/config/update', {
          storeId: this.editForm.storeId,
          isEnabled: this.editForm.isEnabled === 1,
          cardQuotas: this.editForm.cardQuotas, // 发送动态卡片额度配置
          updateBy: window.sessionStorage.getItem('admin_token') || 'admin'
        })
        if (res.data.status === 0) {
          this.$message.success('更新成功')
          this.editDialogVisible = false
          this.loadConfigList()
        } else {
          this.$message.error(res.data.message || '更新失败')
        }
      } catch (e) {
        console.error('更新失败', e)
        this.$message.error('更新失败')
      } finally {
        this.submitting = false
      }
    },

    async handleStatusChange(row) {
      try {
        const res = await axios.post('/cardIssue/config/update', {
          storeId: row.storeId,
          isEnabled: row.isEnabled === 1,
          updateBy: window.sessionStorage.getItem('admin_token') || 'admin'
        })
        if (res.data.status === 0) {
          this.$message.success(row.isEnabled === 1 ? '已开启' : '已关闭')
        } else {
          this.$message.error(res.data.message || '操作失败')
          row.isEnabled = row.isEnabled === 1 ? 0 : 1
        }
      } catch (e) {
        console.error('操作失败', e)
        this.$message.error('操作失败')
        row.isEnabled = row.isEnabled === 1 ? 0 : 1
      }
    },

    handleDelete(row) {
      this.$confirm(`确定要删除店铺"${row.storeName}"的发卡配置吗？删除后该店铺将无法使用发卡功能。`, '提示', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await axios.post('/cardIssue/config/delete', {
            storeId: row.storeId
          })
          if (res.data.status === 0) {
            this.$message.success('删除成功')
            this.loadConfigList()
            this.loadAllStores()
          } else {
            this.$message.error(res.data.message || '删除失败')
          }
        } catch (e) {
          console.error('删除失败', e)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },

    formatTime(timeStr) {
      if (!timeStr) return ''
      const date = new Date(timeStr)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    }
  }
}
</script>

<style scoped>
.card-issue-config {
  padding: 20px;
}

.box-card {
  margin-top: 20px;
}

.header-actions {
  margin-bottom: 20px;
}
</style>
